import { useConfirmationStore } from '@/components/Confirmation/ConfirmationStore';
import { useMoveToFolder } from '@/hooks/useMoveToFolder';
import { type WorkspaceItem, useWorkspace } from '@/hooks/useWorkspace';
import { SortableTreeItem } from '@/lib/tree/SortableTreeItem';
import type { FlattenedItem } from '@/lib/tree/types';
import { DndContext, DragOverlay, useSensor, useSensors } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import type React from 'react';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const indentationWidth = 16; // Keep consistent with SortableTree default

interface DragSidebarProps {
  children: React.ReactNode;
}

interface DragSidebarInternalProps {
  children: React.ReactNode;
  activeId: string | null; // active.id is a string in this app
}

export const lastHoveredSidebarGroupAtom = atom<string | null>(null);
export const useLastHoveredSidebarGroup = () => {
  const setSidebarGroup = useSetAtom(lastHoveredSidebarGroupAtom);

  const handleMouseEnter = (id: string) => {
    setSidebarGroup(id);
  };

  const handleMouseLeave = () => {
    setSidebarGroup(null);
  };
  return { handleMouseEnter, handleMouseLeave };
};

export const selectedIdsAtom = atom<string[]>([]);
export const useSelectedIds = () => {
  const setSelectedIds = useSetAtom(selectedIdsAtom);
  return { setSelectedIds };
};

export const DragSidebarInternal = ({
  children,
  activeId,
}: DragSidebarInternalProps) => {
  const { data: workspaceData } = useWorkspace();
  const items = useMemo<WorkspaceItem[]>(
    () => workspaceData?.entities || [],
    [workspaceData],
  );

  const activeItem = useMemo<FlattenedItem | null>(() => {
    if (!activeId) return null;

    const makeFlattened = (
      base: WorkspaceItem & { depth: number },
    ): FlattenedItem => {
      return {
        ...base,
        // Provide properties expected by FlattenedItem but not present in WorkspaceItem
        children:
          (base as unknown as { children?: FlattenedItem[] }).children ?? [],
        index: 0,
        parentId: null,
        collapsed: false,
      } as unknown as FlattenedItem;
    };

    const foundItem = items.find((item) => item.id === activeId);
    if (foundItem) {
      return makeFlattened({ ...foundItem, depth: 0 });
    }

    // Search inside chats if not a top-level entity
    for (const entity of items) {
      if (entity.chats) {
        const chatItem = entity.chats.find((chat) => chat.id === activeId);
        if (chatItem) {
          return makeFlattened({
            ...(chatItem as unknown as WorkspaceItem),
            depth: 1,
            type: 'CHAT',
          });
        }
      }
    }
    return null;
  }, [activeId, items]);

  return (
    <>
      {children}
      {typeof window !== 'undefined' &&
        createPortal(
          <DragOverlay
            modifiers={[snapCenterToCursor]}
            className="pointer-events-none"
            dropAnimation={null}
          >
            {activeItem &&
            activeItem.type !== 'WORKSPACE' &&
            activeItem.type !== 'CHAT' ? (
              <div className="relative max-w-[200px]">
                <SortableTreeItem
                  tree={activeItem as FlattenedItem}
                  depth={activeItem.depth || 0}
                  clone
                  childCount={
                    'chats' in activeItem &&
                    Array.isArray(
                      (activeItem as unknown as WorkspaceItem).chats,
                    )
                      ? (activeItem as unknown as WorkspaceItem).chats.length
                      : 0
                  }
                  indentationWidth={indentationWidth}
                />
              </div>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
    </>
  );
};

import {
  MouseSensor as LibMouseSensor,
  TouchSensor as LibTouchSensor,
} from '@dnd-kit/core';
import type { MouseEvent, TouchEvent } from 'react';

// Block DnD event propagation if element have "data-no-dnd" attribute
const handler = ({ nativeEvent: event }: MouseEvent | TouchEvent) => {
  let cur = event.target as HTMLElement;

  while (cur) {
    if (cur.dataset?.noDnd) {
      return false;
    }
    cur = cur.parentElement as HTMLElement;
  }

  return true;
};

export class MouseSensor extends LibMouseSensor {
  static activators = [
    { eventName: 'onMouseDown', handler },
  ] as (typeof LibMouseSensor)['activators'];
}

export class TouchSensor extends LibTouchSensor {
  static activators = [
    { eventName: 'onTouchStart', handler },
  ] as (typeof LibTouchSensor)['activators'];
}

export const DragSidebar = ({ children }: DragSidebarProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      distance: 5,
    },
  });

  const { mutate: moveToFolder } = useMoveToFolder();
  const { data: workspaceData } = useWorkspace();
  const confirm = useConfirmationStore((state) => state.openConfirmation);

  const selectedIds = useAtomValue(selectedIdsAtom);
  const lastHoveredSidebarGroup = useAtomValue(lastHoveredSidebarGroupAtom);

  const sensors = useSensors(mouseSensor, touchSensor);

  return (
    <DndContext
      measuring={{
        draggable: {
          measure: (node) => {
            return node.getBoundingClientRect();
          },
        },
      }}
      sensors={sensors}
      onDragStart={(event) => {
        setActiveId(event.active.id as string);
      }}
      onDragEnd={() => {
        // If a sidebar group (folder) was hovered during drag, move the dragged items into it
        if (lastHoveredSidebarGroup && workspaceData) {
          // Determine which IDs should be moved: if `selectedIds` is empty, use the single `activeId` otherwise the multi-selection
          const initialIds =
            selectedIds.length > 0 ? selectedIds : activeId ? [activeId] : [];

          // Filter out chats - only allow non-chat items to be moved
          const idsToMove = initialIds.filter((id) => {
            const item = workspaceData.entities.find(
              (entity) => entity.id === id,
            );
            return item && item.type !== 'CHAT';
          });

          if (idsToMove.length > 0) {
            // Get the target folder
            const targetFolder = workspaceData.entities.find(
              (item) => item.id === lastHoveredSidebarGroup,
            );

            // Get the items being moved (these are files, not folders or chats)
            const itemsBeingMoved = workspaceData.entities.filter((item) =>
              idsToMove.includes(item.id),
            );

            // Check if all items are from the same group and moving to the same group
            const currentGroupIds = itemsBeingMoved.map(
              (item) => item.groupId || null,
            );
            const allFromSameGroup = currentGroupIds.every(
              (groupId) => groupId === currentGroupIds[0],
            );

            if (
              allFromSameGroup &&
              currentGroupIds[0] === lastHoveredSidebarGroup
            ) {
              return;
            }

            // Check if any item is moving between team and private
            const targetTeamId = targetFolder?.teamId || null;

            // For each file being moved, check its current parent folder's teamId
            const isAnyMovingFromTeamToPrivate = itemsBeingMoved.some(
              (item) => {
                // Find the current parent folder of this file
                const currentParentFolder = item.groupId
                  ? workspaceData.entities.find(
                      (entity) => entity.id === item.groupId,
                    )
                  : null;

                // If file is in a team folder and moving to a private folder
                const currentTeamId = currentParentFolder?.teamId || null;
                return currentTeamId && !targetTeamId;
              },
            );

            const isAnyMovingFromPrivateToTeam = itemsBeingMoved.some(
              (item) => {
                // Find the current parent folder of this file
                const currentParentFolder = item.groupId
                  ? workspaceData.entities.find(
                      (entity) => entity.id === item.groupId,
                    )
                  : null;

                // If file is in a private folder and moving to a team folder
                const currentTeamId = currentParentFolder?.teamId || null;
                return !currentTeamId && targetTeamId;
              },
            );

            const performMove = () => {
              moveToFolder({
                ids: idsToMove,
                folderId: lastHoveredSidebarGroup,
                teamId: targetTeamId,
              });
            };

            if (isAnyMovingFromTeamToPrivate) {
              confirm({
                title: 'Make these items private?',
                description:
                  'Only you will be able to access and edit these items and any nested content.',
                actionLabel: 'Confirm',
                onAction: performMove,
                cancelLabel: 'Cancel',
                onCancel: () => {},
              });
            } else if (isAnyMovingFromPrivateToTeam) {
              confirm({
                title: 'Share items with team?',
                description:
                  'Anyone in your team will be able to access and edit these items and any nested content.',
                actionLabel: 'Confirm',
                onAction: performMove,
                cancelLabel: 'Cancel',
                onCancel: () => {},
              });
            } else {
              performMove();
            }
          }
        }

        setActiveId(null);
      }}
      onDragCancel={() => {
        setActiveId(null);
      }}
    >
      <DragSidebarInternal activeId={activeId}>{children}</DragSidebarInternal>
    </DndContext>
  );
};
